import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import apiWrapper from "merchantApiClientWrapper";
import axios from "AxiosInstance";
import { getProductSetupHint } from "./setupMessages";

/** Auth pages where the setup guide must stay hidden (even if session keys exist). */
function isPublicAuthRoute(pathname) {
  return (
    pathname === "/" ||
    pathname === "/forgot" ||
    pathname.startsWith("/resetpasswordwithtoken")
  );
}

/**
 * Shared onboarding state for the Setup Guide.
 *
 * Completion is merchant-scoped and survives logout (see preserveSetupGuideKeys
 * used by SideBar logout). Soft steps (settings / webshop) are also detected
 * from live APIs so wiping local ack can't force the panel back forever.
 */

const LS_PREFIX = "kuber-setup-guide-v1";

function storageKeyForMerchant() {
  const merchantId = localStorage.getItem("merchantId") || "anon";
  return `${LS_PREFIX}:${merchantId}`;
}

function loadState() {
  try {
    // Prefer merchant-scoped key; migrate legacy global key once.
    const scoped = localStorage.getItem(storageKeyForMerchant());
    if (scoped) return JSON.parse(scoped) || {};
    const legacy = localStorage.getItem(LS_PREFIX);
    if (legacy) {
      const parsed = JSON.parse(legacy) || {};
      localStorage.setItem(storageKeyForMerchant(), legacy);
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
}

function saveState(state) {
  try {
    localStorage.setItem(storageKeyForMerchant(), JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** Call before localStorage.clear() on logout so setup progress isn't wiped. */
export function preserveSetupGuideKeys() {
  try {
    const backup = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LS_PREFIX)) {
        backup[key] = localStorage.getItem(key);
      }
    }
    return backup;
  } catch {
    return {};
  }
}

export function restoreSetupGuideKeys(backup) {
  if (!backup || typeof backup !== "object") return;
  try {
    Object.entries(backup).forEach(([key, value]) => {
      if (value != null) localStorage.setItem(key, value);
    });
  } catch {
    /* ignore */
  }
}

const PREVIEW_KEY = "kuber-setup-guide-preview";
function envPreviewEnabled() {
  const v = String(process.env.REACT_APP_SETUP_GUIDE_PREVIEW || "").toLowerCase();
  return v === "true" || v === "1" || v === "yes" || v === "on";
}
function readPreview() {
  try {
    if (envPreviewEnabled()) return true;
    const q = new URLSearchParams(window.location.search).get("setupguide");
    if (q === "preview") localStorage.setItem(PREVIEW_KEY, "1");
    if (q === "off") localStorage.removeItem(PREVIEW_KEY);
    return localStorage.getItem(PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

const AI_BUILDER_URL =
  process.env.REACT_APP_AI_BUILDER_URL || "http://localhost:4000";

export const SETUP_STEPS = [
  {
    id: "settings",
    title: "Set up your store",
    copy: "First, let's make it yours - your store name, currency and the basics. Everything else fits around this.",
    route: "/settings",
    soft: true,
  },
  {
    id: "brands",
    title: "Add a brand",
    copy: "Add the brands you stock (like Nike or Puma). Every product belongs to a brand, so this comes first.",
    route: "/catalogue/brands/add",
    noSkip: true,
  },
  {
    id: "categories",
    title: "Create categories",
    copy: "Set up categories so shoppers can browse - for example Apparel → T‑Shirts. You can nest them too.",
    route: "/catalogue/categories",
    noSkip: true,
  },
  {
    id: "attributes",
    title: "Set up attributes",
    copy: "Selling items with sizes or colours? Add attributes first so you can generate product variants.",
    route: "/catalogue/attributes",
    optional: true,
    noSkip: true,
  },
  {
    id: "products",
    title: "Add your first product",
    copy: "The exciting part! Add a product - drop in a photo and I can draft the title, description and category for you. ✨",
    route: "/catalogue/products/add",
    noSkip: true,
  },
  {
    id: "webshop",
    title: "Create your webshop",
    copy: "Last step - build your storefront. Design it with AI from a short description, or start from a ready-made template. Then you're live! 🚀",
    route: "/templates",
    soft: true,
  },
];

const STEP_ROUTE_PREFIX = {
  settings: "/settings",
  brands: "/catalogue/brands",
  categories: "/catalogue/categories",
  attributes: "/catalogue/attributes",
  products: "/catalogue/products",
  webshop: "/templates",
};

function stepMatchesRoute(step, pathname) {
  const prefix = STEP_ROUTE_PREFIX[step.id] || step.route;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function hasCatalogItems(resp) {
  const d = resp && resp.data;
  if (Array.isArray(d)) return d.length > 0;
  if (d && Array.isArray(d.data)) return d.data.length > 0;
  if (d && Array.isArray(d.items)) return d.items.length > 0;
  if (d && typeof d.count === "number") return d.count > 0;
  if (d && typeof d.totalCount === "number") return d.totalCount > 0;
  return false;
}

async function detectSettingsDone() {
  try {
    const res = await axios.get("Merchant/GetCurrentMerchant");
    const m = res?.data || {};
    const name =
      m.companyName ||
      m.storeName ||
      m.businessName ||
      m.name ||
      m.merchantName ||
      "";
    return Boolean(String(name).trim()) && !/^my store$/i.test(String(name).trim());
  } catch {
    return false;
  }
}

async function detectWebshopDone() {
  const merchantId = localStorage.getItem("merchantId");
  const sessionId = localStorage.getItem("sessionId");
  if (!merchantId || !sessionId) return false;
  try {
    const res = await fetch(`${AI_BUILDER_URL}/api/shops/mine`, {
      cache: "no-store",
      headers: { merchantId, sessionId },
    });
    if (!res.ok) return false;
    const payload = await res.json();
    if (payload?.shopConfig?.templateRepoName) return true;
    const sessions = Array.isArray(payload?.aiSessions) ? payload.aiSessions : [];
    return sessions.some((s) =>
      ["previewing", "published", "generating"].includes(s.status)
    );
  } catch {
    return false;
  }
}

export function useSetupProgress() {
  const { pathname } = useLocation();
  const hasSession =
    !!localStorage.getItem("merchantId") && !!localStorage.getItem("sessionId");
  const loggedIn = hasSession && !isPublicAuthRoute(pathname);

  const [state, setState] = useState(loadState);
  const [preview] = useState(readPreview);
  const [detected, setDetected] = useState({
    brands: false,
    categories: false,
    attributes: false,
    products: false,
    settings: false,
    webshop: false,
    loading: true,
  });

  // Reload merchant-scoped state when session becomes available after login.
  useEffect(() => {
    if (!loggedIn) return;
    setState(loadState());
  }, [loggedIn]);

  const persist = useCallback((next) => {
    setState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      saveState(resolved);
      return resolved;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!loggedIn) return;
    if (preview) {
      setDetected({
        brands: false,
        categories: false,
        attributes: false,
        products: false,
        settings: false,
        webshop: false,
        loading: false,
      });
      return;
    }
    const has = (p) => p.then(hasCatalogItems).catch(() => false);
    const [brands, categories, attributes, products, settings, webshop] =
      await Promise.all([
        has(apiWrapper.brand.getAllbrand()),
        has(apiWrapper.category.getAllCategory()),
        has(apiWrapper.attribute.getAllattribute()),
        has(apiWrapper.MasterProduct.getAll()),
        detectSettingsDone(),
        detectWebshopDone(),
      ]);
    setDetected({
      brands,
      categories,
      attributes,
      products,
      settings,
      webshop,
      loading: false,
    });
  }, [loggedIn, preview]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Soft steps: also ack when the merchant navigates there.
  useEffect(() => {
    if (!loggedIn) return;
    const softOnRoute = SETUP_STEPS.find((s) => s.soft && stepMatchesRoute(s, pathname));
    if (!softOnRoute) return;
    persist((prev) => {
      if (prev.ack?.[softOnRoute.id] || prev.skipped?.[softOnRoute.id]) return prev;
      return { ...prev, ack: { ...(prev.ack || {}), [softOnRoute.id]: true } };
    });
  }, [loggedIn, pathname, persist]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  const isDone = useCallback(
    (step) => {
      if (state.completed) return true;
      if (state.ack && state.ack[step.id]) return true;
      if (preview) return false;
      // Soft steps: use live detection as well as local ack (survives logout).
      if (step.id === "settings") return !!detected.settings;
      if (step.id === "webshop") return !!detected.webshop;
      if (step.soft) return false;
      return !!detected[step.id];
    },
    [preview, detected, state.ack, state.completed]
  );

  const isSkipped = useCallback(
    (step) => !step.noSkip && !!(state.skipped && state.skipped[step.id]),
    [state.skipped]
  );

  const handled = useCallback((step) => isDone(step) || isSkipped(step), [isDone, isSkipped]);

  const requiredSteps = useMemo(
    () => SETUP_STEPS.filter((s) => !s.optional || state.sellsVariants),
    [state.sellsVariants]
  );
  const doneCount = requiredSteps.filter(handled).length;
  const total = requiredSteps.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const allDone =
    !!state.completed ||
    !!state.dismissed ||
    (total > 0 && doneCount === total && !detected.loading);
  const currentStep = requiredSteps.find((s) => !handled(s)) || null;

  // Persist a durable "completed" flag once everything is done so logout/login
  // can't revive the panel because of a soft-ack miss.
  useEffect(() => {
    if (!loggedIn || preview || detected.loading || state.completed || state.dismissed) return;
    if (total > 0 && doneCount === total) {
      persist((prev) => ({ ...prev, completed: true, seen: true }));
    }
  }, [
    loggedIn,
    preview,
    detected.loading,
    doneCount,
    total,
    state.completed,
    state.dismissed,
    persist,
  ]);

  const activeStep = useMemo(() => {
    const onRoute = SETUP_STEPS.find((s) => stepMatchesRoute(s, pathname));
    return onRoute || currentStep;
  }, [pathname, currentStep]);

  const markDone = useCallback(
    (step) => {
      persist((prev) => {
        const skipped = { ...(prev.skipped || {}) };
        delete skipped[step.id];
        return { ...prev, ack: { ...(prev.ack || {}), [step.id]: true }, skipped };
      });
    },
    [persist]
  );

  const skipStep = useCallback(
    (step) => {
      if (step.noSkip) return;
      persist((prev) => ({
        ...prev,
        skipped: { ...(prev.skipped || {}), [step.id]: true },
      }));
    },
    [persist]
  );

  const unskip = useCallback(
    (step) => {
      persist((prev) => {
        const skipped = { ...(prev.skipped || {}) };
        delete skipped[step.id];
        return { ...prev, skipped };
      });
    },
    [persist]
  );

  const toggleVariants = useCallback(() => {
    persist((prev) => ({ ...prev, sellsVariants: !prev.sellsVariants }));
  }, [persist]);

  const dismiss = useCallback(() => {
    persist((prev) => ({ ...prev, dismissed: true, seen: true, completed: true }));
  }, [persist]);

  const restart = useCallback(() => {
    persist({ ack: {}, skipped: {}, sellsVariants: state.sellsVariants, seen: true, completed: false, dismissed: false });
    refresh();
  }, [persist, refresh, state.sellsVariants]);

  const productSetupHint = useMemo(
    () =>
      getProductSetupHint({
        state,
        preview,
        detected,
        sellsVariants: !!state.sellsVariants,
        steps: SETUP_STEPS,
      }),
    [state, preview, detected]
  );

  return {
    loggedIn,
    preview,
    state,
    persist,
    detected,
    refresh,
    steps: SETUP_STEPS,
    isDone,
    isSkipped,
    handled,
    requiredSteps,
    doneCount,
    total,
    pct,
    allDone,
    currentStep,
    activeStep,
    sellsVariants: !!state.sellsVariants,
    markDone,
    skipStep,
    unskip,
    toggleVariants,
    restart,
    dismiss,
    productSetupHint,
  };
}
